import React, { useEffect, useState } from "react";
import api, { ASSET } from "../../lib/api";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { FileCheck2, FileX2, FileText, Building2, Mail, CheckCircle2, Download } from "lucide-react";

const StatusPill = ({ ok }) =>
  ok ? (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600">
      <FileCheck2 className="h-4 w-4" /> Caricato
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-500">
      <FileX2 className="h-4 w-4" /> Mancante
    </span>
  );

export default function AdminVendorDocuments() {
  const [data, setData] = useState({ vendors: [], total: 0, complete: 0 });
  const [loading, setLoading] = useState(true);
  const [onlyIncomplete, setOnlyIncomplete] = useState(false);

  const reload = () => {
    setLoading(true);
    api.get("/admin/vendor/documents-status")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Errore caricamento"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, []);

  const vendors = onlyIncomplete
    ? data.vendors.filter((v) => !(v.has_contratto && v.has_liberatoria))
    : data.vendors;

  return (
    <div data-testid="admin-vendor-documents-page" className="container-xl py-8">
      <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--accent))] mb-2">Super Admin · Fornitori</div>
      <h1 className="editorial-heading text-4xl flex items-center gap-3">
        <FileText className="h-8 w-8 text-[hsl(var(--primary))]" />Stato Documenti Aziende
      </h1>
      <p className="text-muted-foreground mt-2 mb-6">
        Quali aziende affiliate hanno caricato il <strong>Contratto di Licenza firmato</strong> e la <strong>Liberatoria</strong>.
      </p>

      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="bg-card border border-border p-4 rounded-sm">
          <div className="text-xs text-muted-foreground uppercase tracking-wider">Aziende</div>
          <div className="text-2xl font-bold mt-1">{data.total}</div>
        </div>
        <div className="bg-card border border-border p-4 rounded-sm">
          <div className="text-xs text-muted-foreground uppercase tracking-wider flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />Complete</div>
          <div className="text-2xl font-bold mt-1 text-emerald-600">{data.complete}</div>
        </div>
        <div className="border border-amber-500/30 bg-amber-500/10 p-4 rounded-sm">
          <div className="text-xs uppercase tracking-wider text-amber-600">Da completare</div>
          <div className="text-2xl font-bold mt-1 text-amber-600">{data.total - data.complete}</div>
        </div>
      </div>

      <label className="inline-flex items-center gap-2 mb-6 text-sm cursor-pointer">
        <input type="checkbox" data-testid="only-incomplete" checked={onlyIncomplete} onChange={(e) => setOnlyIncomplete(e.target.checked)} />
        Mostra solo aziende con documenti mancanti
      </label>

      {loading ? (
        <div className="text-center text-muted-foreground py-12">Caricamento...</div>
      ) : vendors.length === 0 ? (
        <div className="bg-card border border-border p-12 text-center rounded-sm">
          <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
          <div className="text-lg font-semibold">Nessuna azienda da mostrare</div>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-sm overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Azienda</TableHead>
                <TableHead>Contratto di Licenza</TableHead>
                <TableHead>Liberatoria</TableHead>
                <TableHead>Documenti caricati</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {vendors.map((v) => (
                <TableRow key={v.vendor_id} data-testid={`vendor-doc-${v.vendor_id}`}>
                  <TableCell className="max-w-[260px]">
                    <div className="font-semibold truncate flex items-center gap-1.5">
                      <Building2 className="h-3.5 w-3.5 text-muted-foreground" />{v.company}
                    </div>
                    {v.email && (
                      <a href={`mailto:${v.email}`} className="text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-1">
                        <Mail className="h-3 w-3" />{v.email}
                      </a>
                    )}
                  </TableCell>
                  <TableCell><StatusPill ok={v.has_contratto} /></TableCell>
                  <TableCell><StatusPill ok={v.has_liberatoria} /></TableCell>
                  <TableCell>
                    {v.documents.length === 0 ? (
                      <span className="text-xs text-muted-foreground">—</span>
                    ) : (
                      <div className="flex flex-col gap-1">
                        {v.documents.map((d) => (
                          <a
                            key={d.id}
                            href={ASSET(d.url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs text-[hsl(var(--primary))] hover:underline"
                          >
                            <Download className="h-3 w-3" />
                            <span className="truncate max-w-[220px]">{d.label} <span className="text-muted-foreground">({d.doc_type})</span></span>
                          </a>
                        ))}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
